
import { Injectable  }           from '@angular/core';
import { HttpClient, HttpParams }  from '@angular/common/http';
import { HttpHeaders }           from '@angular/common/http';
import { AuthenticationService } from './authentication.service';

import { User }                  from '@/models/user';
import { first }                 from "rxjs/operators";
import { Logger }                from "@/util/logger";
import {BatchCell} from "@/models/batch/batch.cell";

import {
  Observable
}                                 from 'rxjs';

@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class ScalableWorkEngineService
 *
 * Remote access to Scalable Work Engine (SWE) within our cluster
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      February 2022
 *
 */
export class ScalableWorkEngineService {

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService
  ) {

  }

  /** fetch any headers we need to send along with our request */
  headers(): HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return new HttpHeaders().set('Authorization', `Bearer ${user.token}`);
  }

  /** convert a string into a binary form but 8-bit ascii encoded.  Allow for unicode etc. */
  binaryKey(key: string): string {

    /* ref: https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings */

    const codeUnits = new Uint16Array(key.length);

    let byteArray: number[] = [];

    for (let i = 0; i < codeUnits.length; i++) {

      let byte: number = key.charCodeAt(i);

      /* low byte first */

      byteArray.push(byte & 0xFF);

      /* high byte second */

      byteArray.push((byte >> 8) & 0xFF);
    }

    return btoa(String.fromCharCode(...byteArray));
  }

  /**
   *  helper to generate the final summary data for batch via its KPI and historical regressions
   *  for the same corridors in that "batch of batches" or "batch of corridors".
   *
   */
  public batchSummarize(rollup: string, regressions: string[]): Promise<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    Logger.debug(`[swe] summarizing batch: ${rollup})...`, regressions);

    return new Promise((resolve, reject) => {

      this.http.post<string>(`/backend/swe/corridors/summarize`,
        {
          regressions,
          rollup
        },
        {
          headers
        }).pipe(first())
        .subscribe(
          (data: string) => {

            /*
             * we don't have to JSON parse; backend gives us back JSON format, it gets
             * decoded automatically.
             *
             */

            /* pass back the control file as an object */

            resolve(data);
          },
          (error) => {

            Logger.error('[swe] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });
  }

  /** scan the given corridor batch import folder and generate the "control file" */
  public corridorImportScan(key: string, isPlan: boolean): Promise<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return new Promise((resolve, reject) => {

      const params  = new HttpParams()
        .set('key',      key)
        .set('bkey',     this.binaryKey(key))
        .set('plan',     isPlan ? 'true' : 'false');

      Logger.debug(`[swe] scanning corridors for import (${key} | ${isPlan ? 'true' : 'false'})...`);

      this.http.get<string>(`/backend/swe/corridors/scan`, {
        headers,
        params
      }).pipe(first())
        .subscribe(
          (control: string) => {

            /*
             * we don't have to JSON parse; backend gives us back JSON format, it gets
             * decoded automatically.
             *
             */

            Logger.debug('[swe] control file: ', control);

            /* pass back the control file as an object */

            resolve(control);
          },
          (error) => {

            Logger.error('[swe] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });


  }

  /** fetch the raw batch definition */
  public batch(batch: string): Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    Logger.debug(`[swe] fetching batch: ${batch}...`);

    return this.http.get<string>(`/backend/swe/batch/${batch}`, {
      headers
    });
  }

  /** delete (entirely including results) a batch on the SWE side */
  public deleteBatch(batch: string) : Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    Logger.debug(`[swe] deleting batch: ${batch}...`);

    return this.http.delete<string>(`/backend/swe/batch/delete/${batch}`, {
        headers
      });
  }

  /** helper to turn on or off the given cells */
  public forceCellStatus(cells: BatchCell[], enabled: boolean): Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    let manifest: any[] = [];

    /* format the cells as needed by the SWE */

    cells.forEach((item: BatchCell) => {
      manifest.push({
        id:      item.id,
        status:  enabled ? 'enabled' : 'disabled'
      });

    });

    /* do it! */

    Logger.debug(`[swe] modifying ${manifest.length} cells...`, manifest);

    return this.http.put<string>(`/backend/swe/cells/modify`,
      {
        manifest
      },
      {
        headers
      });

  }

  /** both import and create a batch at the same time; the cells give the definition for what to import */
  public importCorridorBatch(batch: string, corridor: string, root: string, cells: BatchCell[], batchMeta: any): Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    const name: string = batch + '_' + corridor;

    const params  = new HttpParams()
      .set('root',      root)
      .set('broot',     this.binaryKey(root))
      .set('name',      name)
      .set('bname',     this.binaryKey(name));

    let manifest: any[] = [];

    /* format the cells as needed by the SWE */

    cells.forEach((item: BatchCell) => {
      manifest.push({
        name:    item.name,
        status:  item.enabled ? 'enabled' : 'disabled',
        plant:   item.plantSrc,
        traffic: item.trafficSrc,
        meta:    item.meta
      });

    });

    /* do it! */

    Logger.debug(`[swe] importing batch: ${name} to: ${root})...`, manifest);

    return this.http.post<string>(`/backend/swe/corridors/import`,
        {
        meta: batchMeta,
        manifest
      },
        {
        headers,
        params
      });

  }


  /** create a new empty result cloned from this batch */
  public spawn(batch: string, name: string) : Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    Logger.debug(`[swe] spawning result: ${name} via batch ${batch})...`);

    return this.http.post<string>(`/backend/swe/spawn/${batch}`,
      {
        name
      },
      {
        headers
      });
  }

  /** fetch the KPI rollup for this batch result */
  public allKPI(result: string, key: string, corridor: string): Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    const params  = new HttpParams()
      .set('key',      key)
      .set('bkey',     this.binaryKey(key))
      .set('c',      corridor)
      .set('bc',     this.binaryKey(corridor));

    Logger.debug(`[swe] fetching all KPI of [${corridor}]: ${result}...`);

    return this.http.get<string>(`/backend/swe/result/all/kpi/${result}`, {
      headers,
      params
    });

  }

  /** fetch full details for a result */
  public result(result: string): Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    Logger.debug(`[swe] fetching result: ${result}...`);

    return this.http.get<string>(`/backend/swe/result/${result}`, {
      headers
    });
  }

  /** fetch result summaries (progress overview) */
  public summaries(jobIDs: number[]): Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    const params  = new HttpParams().set('jobs',    jobIDs.join(','));

    /* pass back this hits */

    Logger.debug(`[swe] fetching result summaries: ${jobIDs.join(',')}...`);

    return this.http.get(`/backend/swe/job/status`, {
      headers,
      params
    });
  }

  /** trigger a batch to start processing */
  public trigger(result: string): Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    Logger.debug(`[swe] triggering: ${result}...`);

    return this.http.put<string>(`/backend/swe/result/trigger/${result}`, {
      headers
    });
  }

  /** delete (entirely) a result on the SWE side */
  public deleteResult(result: string) : Observable<any> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /* do it! */

    Logger.debug(`[swe] deleting result: ${result}...`);

    return this.http.delete<string>(`/backend/swe/result/delete/${result}`, {
      headers
    });
  }



}
